import { DsmPageContainer } from '@/components';
import PageHeader from '@/components/PageHeader';
import {
  AccountctlTestUser,
  useGetAccountctlTestUsersQuery,
  usePutAccountctlTestUsersMutation,
  useTestAccountctlTestUserMutation,
} from '@/services/accountctlApi';
import { PalmettoButton, PalmettoIcon, PalmettoSelect, PalmettoTextInput } from '@palmetto/dsm-components';
import { toast } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';

import { HarnessRole } from 'types';

interface TestResult {
  ok: boolean;
  message?: string;
  organizationAlias?: string;
}

interface TestUserRowProps {
  index: number;
  user: AccountctlTestUser;
  passwordVisible: boolean;
  testResult: TestResult | undefined;
  isTesting: boolean;
  onChange: (index: number, next: AccountctlTestUser) => void;
  onRemove: (index: number) => void;
  onTogglePassword: (index: number) => void;
  onTest: (index: number) => void;
}

const roleItems: Array<{ label: string; value: HarnessRole }> = [
  { label: 'EPC', value: 'epc' },
  { label: 'Internal Reviewer', value: 'internal-reviewer' },
];

const TestUserRow = ({
  index,
  user,
  passwordVisible,
  testResult,
  isTesting,
  onChange,
  onRemove,
  onTogglePassword,
  onTest,
}: TestUserRowProps) => {
  const handleLabel = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(index, { ...user, label: event.target.value });
    },
    [index, onChange, user],
  );

  const handleEmail = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(index, { ...user, email: event.target.value });
    },
    [index, onChange, user],
  );

  const handlePassword = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(index, { ...user, password: event.target.value });
    },
    [index, onChange, user],
  );

  const handleOrgAlias = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      onChange(index, { ...user, organizationAlias: value || undefined });
    },
    [index, onChange, user],
  );

  const handleRoleChange = useCallback(
    (details: { value: string[] }) => {
      const role = details.value[0] as HarnessRole | undefined;
      if (!role) return;
      onChange(index, { ...user, role });
    },
    [index, onChange, user],
  );

  const handleTogglePassword = useCallback(() => {
    onTogglePassword(index);
  }, [index, onTogglePassword]);

  const handleRemoveClick = useCallback(() => {
    onRemove(index);
  }, [index, onRemove]);

  const handleTestClick = useCallback(() => {
    onTest(index);
  }, [index, onTest]);

  const roleOptionItems = roleItems.map((item) => ({
    label: item.label,
    value: item.value,
    selected: item.value === user.role,
  }));

  return (
    <div className="flex flex-col gap-16 rounded-6 border border-border-default p-16">
      <div className="grid grid-cols-1 gap-16 md:grid-cols-2">
        <PalmettoTextInput label="Label" value={user.label} onChange={handleLabel} />
        <PalmettoSelect label="Role" items={roleOptionItems} onValueChange={handleRoleChange} />
        <PalmettoTextInput label="Email" value={user.email} onChange={handleEmail} />
        <div className="flex items-end gap-8">
          <div className="flex-1">
            <PalmettoTextInput
              label="Password"
              type={passwordVisible ? 'text' : 'password'}
              value={user.password}
              onChange={handlePassword}
            />
          </div>
          <PalmettoButton type="button" emphasis="secondary" size="small" onClick={handleTogglePassword}>
            {passwordVisible ? 'Hide' : 'Show'}
          </PalmettoButton>
        </div>
        <PalmettoTextInput
          label="Organization Alias (optional)"
          value={user.organizationAlias ?? ''}
          onChange={handleOrgAlias}
        />
      </div>
      <div className="flex items-center gap-8">
        <PalmettoButton
          type="button"
          emphasis="secondary"
          size="small"
          onClick={handleTestClick}
          disabled={isTesting}
        >
          {isTesting ? 'Testing…' : 'Test credentials'}
        </PalmettoButton>
        <PalmettoButton type="button" emphasis="secondary" size="small" onClick={handleRemoveClick}>
          Remove
        </PalmettoButton>
        {testResult && (
          <div
            className={`flex items-center gap-4 text-sm ${testResult.ok ? 'text-text-success' : 'text-text-error'}`}
            role="status"
          >
            <PalmettoIcon name={testResult.ok ? 'check' : 'x'} />
            {testResult.ok
              ? `OK${testResult.organizationAlias ? ` (org: ${testResult.organizationAlias})` : ''}`
              : (testResult.message ?? 'Failed')}
          </div>
        )}
      </div>
    </div>
  );
};

export const TestUsersPage = () => {
  const { data: serverUsers, isLoading } = useGetAccountctlTestUsersQuery();
  const [putUsers, { isLoading: isSaving }] = usePutAccountctlTestUsersMutation();
  const [testUser, { isLoading: isTesting }] = useTestAccountctlTestUserMutation();

  const [users, setUsers] = useState<AccountctlTestUser[]>([]);
  // Stable per-row identity for React keys. The rest of this page (and the
  // server's `:index/test` route) is index-addressed, but keying the list by
  // index makes React reuse a removed row's DOM node — and with it the focus
  // and selection — for whichever row slides up into that slot.
  const [rowIds, setRowIds] = useState<string[]>([]);
  const nextRowId = useRef(0);
  const makeRowId = useCallback((): string => `row-${nextRowId.current++}`, []);
  const [passwordVisibility, setPasswordVisibility] = useState<Record<number, boolean>>({});
  const [testResults, setTestResults] = useState<Record<number, TestResult>>({});
  const [testingIndex, setTestingIndex] = useState<number | null>(null);

  // Hydrate the editable list whenever the server snapshot changes (initial
  // load, refetch after save). We intentionally do NOT use `serverUsers` as
  // a direct controlled source — operators need to be able to edit locally
  // without each keystroke round-tripping to the server.
  useEffect(() => {
    if (serverUsers) {
      setUsers(serverUsers);
      setRowIds(serverUsers.map(() => makeRowId()));
    }
  }, [serverUsers, makeRowId]);

  const handleChangeRow = useCallback((index: number, next: AccountctlTestUser) => {
    setUsers((prev) => {
      const copy = [...prev];
      copy[index] = next;
      return copy;
    });
    // Clear any stale test result whenever the row changes.
    setTestResults((prev) => {
      if (!(index in prev)) return prev;
      const copy = { ...prev };
      delete copy[index];
      return copy;
    });
  }, []);

  const handleRemoveRow = useCallback((index: number) => {
    setUsers((prev) => prev.filter((_, i) => i !== index));
    setRowIds((prev) => prev.filter((_, i) => i !== index));
    setTestResults((prev) => {
      // Reindex results > index so they still line up.
      const next: Record<number, TestResult> = {};
      for (const [k, v] of Object.entries(prev)) {
        const i = Number(k);
        if (i < index) next[i] = v;
        else if (i > index) next[i - 1] = v;
      }
      return next;
    });
    setPasswordVisibility((prev) => {
      const next: Record<number, boolean> = {};
      for (const [k, v] of Object.entries(prev)) {
        const i = Number(k);
        if (i < index) next[i] = v;
        else if (i > index) next[i - 1] = v;
      }
      return next;
    });
  }, []);

  const handleTogglePassword = useCallback((index: number) => {
    setPasswordVisibility((prev) => ({ ...prev, [index]: !prev[index] }));
  }, []);

  const handleAdd = useCallback(() => {
    setUsers((prev) => [...prev, { label: '', role: 'epc', email: '', password: '' }]);
    setRowIds((prev) => [...prev, makeRowId()]);
  }, [makeRowId]);

  const handleSave = useCallback(async () => {
    try {
      await putUsers({ users }).unwrap();
      toast.success('Test users saved.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save test users';
      toast.error(message);
    }
  }, [putUsers, users]);

  const handleTestRow = useCallback(
    async (index: number) => {
      // Persist first so the server's `:index/test` endpoint resolves against
      // the same row the operator just edited. We only do this if the local
      // edits differ from the server snapshot — otherwise an unmodified test
      // is just a fast probe against the saved list.
      const isDirty = JSON.stringify(users) !== JSON.stringify(serverUsers ?? []);
      try {
        if (isDirty) {
          await putUsers({ users }).unwrap();
        }
        setTestingIndex(index);
        const result = await testUser({ index }).unwrap();
        setTestResults((prev) => ({
          ...prev,
          [index]: {
            ok: result.ok,
            message: result.error,
            organizationAlias: result.organizationAlias,
          },
        }));
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Test failed';
        setTestResults((prev) => ({ ...prev, [index]: { ok: false, message } }));
      } finally {
        setTestingIndex(null);
      }
    },
    [putUsers, serverUsers, testUser, users],
  );

  return (
    <DsmPageContainer title="Test Account Generator — test users">
      <PageHeader title="Test Account Generator — test users">
        <div className="flex gap-8">
          <Link to="/internal/accountctl">
            <PalmettoButton type="button" emphasis="secondary">
              Back to scenarios
            </PalmettoButton>
          </Link>
          <PalmettoButton type="button" emphasis="primary" onClick={handleSave} disabled={isLoading || isSaving}>
            {isSaving ? 'Saving…' : 'Save'}
          </PalmettoButton>
        </div>
      </PageHeader>

      <div className="mx-6 mb-16 rounded-6 border border-notification-border-warning bg-notification-canvas-warning p-16">
        <div className="font-bold">Plaintext passwords — non-prod only.</div>
        <div className="text-sm">
          Passwords are stored in plaintext and visible to anyone with internal access. These routes are not mounted
          in production, but you must not paste real customer or production credentials here.
        </div>
      </div>

      <div className="mx-6 flex flex-col gap-16">
        {isLoading ? (
          <div className="text-sm text-text-secondary">Loading…</div>
        ) : (
          users.map((user, index) => (
            <TestUserRow
              key={rowIds[index] ?? `row-fallback-${index}`}
              index={index}
              user={user}
              passwordVisible={!!passwordVisibility[index]}
              testResult={testResults[index]}
              isTesting={isTesting && testingIndex === index}
              onChange={handleChangeRow}
              onRemove={handleRemoveRow}
              onTogglePassword={handleTogglePassword}
              onTest={handleTestRow}
            />
          ))
        )}
        <div>
          <PalmettoButton
            type="button"
            emphasis="secondary"
            onClick={handleAdd}
            leadingIcon={<PalmettoIcon name="add" />}
          >
            Add user
          </PalmettoButton>
        </div>
      </div>
    </DsmPageContainer>
  );
};
